import { Text } from "../../../../atoms/Text";
import { cn } from "../../../../lib/cn";
import { formatCurrencyGBPPounds } from "../../../../lib/currency";
import { paletteText } from "./palette";

export interface MoneyProps {
  /**
   * Null or undefined means the amount is withheld from this viewer, not that
   * it is zero — the API nulls money the viewer has no financial permission
   * for. `showValues` is the separate screen-level toggle.
   */
  value: number | null | undefined;
  /** Palette path ("error.main" for deductions) — mapped to a DS status token. */
  color?: string;
  /** Cancelled amounts are struck through instead of tagged. */
  strike?: boolean;
  /**
   * Quiet treatment for money that is a target rather than a booked value
   * (unbooked items, template band totals).
   */
  quiet?: boolean;
}

/** Stands in for a withheld amount. No em dashes anywhere (decision 14). */
const REDACTED = "··";

/**
 * A ledger amount: tabular figures so columns of money align digit for digit.
 * There is deliberately no "£X of £Y" mode — partial booking shows as a raw
 * percentage next to the title and in full on the hover card.
 */
export const Money = ({
  value,
  color,
  strike = false,
  quiet = false,
}: MoneyProps) => {
  const redacted = value == null;
  const shown = quiet ? "text.secondary" : "text.primary";
  const colorClass = redacted
    ? "text-content-disabled"
    : paletteText(color ?? shown);

  return (
    <Text
      as="span"
      variant="inherit"
      aria-label={redacted ? "Value hidden" : undefined}
      className={cn(
        "whitespace-nowrap tabular-nums",
        quiet
          ? "font-normal text-(length:--ledger-text-cell) leading-[1.66]"
          : "font-semibold text-(length:--ledger-text-title) leading-[1.43]",
        colorClass,
        strike && !redacted && "line-through",
      )}
      style={colorClass === undefined ? { color: color } : undefined}
    >
      {redacted ? REDACTED : formatCurrencyGBPPounds(value)}
    </Text>
  );
};
